import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, API_BASE } from "@/lib/api";
import { FileText, ExternalLink, CalendarDays } from "lucide-react";

const BACKEND = API_BASE.replace(/\/api$/, "");
const resolve = (link) => (link && link.startsWith("/api/") ? `${BACKEND}${link}` : link);

const daysUntil = (s) => {
  if (!s) return null;
  let y, mo, d;
  let m = String(s).match(/(\d{1,2})[./](\d{1,2})[./](\d{2,4})/);
  if (m) {
    d = +m[1]; mo = +m[2]; y = +m[3];
    if (y < 100) y += 2000;
  } else {
    m = String(s).match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/); // yyyy-mm-dd
    if (!m) return null;
    y = +m[1]; mo = +m[2]; d = +m[3];
  }
  const due = new Date(y, mo - 1, d);
  if (isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - today.getTime()) / 86400000);
};

export default function Tenders() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/tenders").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  // Hide tenders whose documents are all unavailable (nothing actionable to show)
  const visible = items.filter((n) => n.notification_link || n.corrigendum_link || n.apply_link);

  return (
    <div className="pb-24">
      <section className="border-b border-ink/10 py-20 circuit-bg-light" aria-labelledby="tenders-title">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="tag mb-3">{t("tenders.eyebrow")}</div>
          <h1 id="tenders-title" className="font-display font-black text-brand text-4xl sm:text-5xl lg:text-6xl leading-[1]">
            {t("tenders.title_a")} <span className="text-teal">{t("tenders.title_b")}</span>
          </h1>
          <p className="mt-6 text-ink/75 max-w-3xl leading-relaxed">{t("tenders.intro")}</p>
        </div>
      </section>

      <section className="py-12" aria-label={t("tenders.title_a")}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {visible.length === 0 ? (
            <div className="text-ink/60 font-mono text-sm" data-testid="tenders-empty">{t("tenders.no_items")}</div>
          ) : (
            <ol className="space-y-5 list-none p-0" data-testid="tender-list">
              {visible.map((n, i) => {
                const dl = daysUntil(n.last_date);
                const soon = dl !== null && dl >= 0 && dl <= 7;
                const closed = dl !== null && dl < 0;
                return (
                <li key={n.id}>
                  <article
                    data-testid={`tender-${n.id}`}
                    className={`bg-white border border-ink/10 border-l-4 ${soon ? "border-l-amber" : closed ? "border-l-ink/30" : "border-l-brand"} p-6 lg:p-8 card-hover rise-in rise-in-${(i % 4) + 1}`}
                  >
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      {n.last_date && (
                        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-amber-700">
                          <CalendarDays className="w-3.5 h-3.5" aria-hidden="true" /> {t("tenders.last_date")}: {n.last_date}
                        </span>
                      )}
                      {soon && (
                        <span data-testid={`tender-closing-soon-${n.id}`} className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber text-ink rounded-sm animate-pulse">
                          {dl === 0 ? t("tenders.closing_today") : t("tenders.closing_soon")}
                        </span>
                      )}
                      {closed && (
                        <span data-testid={`tender-closed-${n.id}`} className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-ink/10 text-ink/50 rounded-sm">
                          {t("tenders.closed")}
                        </span>
                      )}
                    </div>
                    <h2 className="font-display font-bold text-brand text-lg lg:text-xl leading-snug">{n.title}</h2>
                    <div className="mt-6 pt-5 border-t border-ink/10 flex flex-wrap gap-3">
                      {n.notification_link && (
                        <a href={resolve(n.notification_link)} target="_blank" rel="noreferrer" data-testid={`tender-notification-${n.id}`}
                           className="inline-flex items-center gap-2 border border-brand text-brand hover:bg-brand hover:text-white px-4 py-2.5 text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" aria-hidden="true" /> {t("tenders.notification")}
                        </a>
                      )}
                      {n.corrigendum_link && (
                        <a href={resolve(n.corrigendum_link)} target="_blank" rel="noreferrer" data-testid={`tender-corrigendum-${n.id}`}
                           className="inline-flex items-center gap-2 border border-amber text-ink hover:bg-amber px-4 py-2.5 text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" aria-hidden="true" /> {t("tenders.corrigendum")}
                        </a>
                      )}
                      {n.apply_link && (
                        <a href={n.apply_link} target="_blank" rel="noreferrer" data-testid={`tender-apply-${n.id}`}
                           className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white px-5 py-2.5 text-sm font-medium transition-colors ml-auto">
                          {t("tenders.apply")} <ExternalLink className="w-4 h-4" aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  </article>
                </li>
                );
              })}
            </ol>
          )}
        </div>
      </section>
    </div>
  );
}
